/**
 * Add to Position Dialog
 *
 * Allows users to add collateral or savings to an existing position.
 * - Token/vault selector from wallet (filtered by compatible collaterals)
 * - Amount input
 * - Toggle between Collateral (enableCollateral) and Savings (no enableCollateral)
 * - Uses EVC batch for the transaction
 */

'use client'

import { usePositionName } from '@/domain/euler/positions/usePositionName'
import { addIntrinsicAPY } from '@/domain/euler/shared/apyUtils'
import { getWagmiReadQueryKeys } from '@/domain/cache/invalidateBalancesCache'
import { ApyChange } from '@/ui/atoms/apy-change/ApyChange'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { formatHealthFactorValue, formatLTV, getHealthStatus } from '@/domain/common/health-factor'
import { usePermit2Preflight } from '@/domain/euler/evc/usePermit2Preflight'
import { WHYPE_ADDRESS_HYPEREVM, isWhypeVault } from '@/domain/euler/nativeHype'
import { useSubaccountStore } from '@/domain/euler/subaccounts'
import { useWalletTokenBalances } from '@/domain/euler/wallet/useWalletTokenBalances'
import { getSubAccount } from '@/domain/evc'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { AddToPositionSuccessView } from './components/AddToPositionSuccessView'
import { TimeoutErrorDisplay } from '@/features/actions/components/TimeoutErrorDisplay'
import { ActionRow } from '@/features/actions/components/action-row/ActionRow'
import type { ActionHandlerState } from '@/features/actions/logic/types'
import { useActionTimeout } from '@/features/actions/logic/useActionTimeout'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import { useBorrowPairs } from '@/features/eborrow/logic/useBorrowPairs'
import { useELend } from '@/features/elend/logic/useELend'
import { assets } from '@/ui/assets'
import { getTokenImage } from '@/ui/assets/tokens'
import { Button } from '@/ui/atoms/button/Button'
import { DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { Switch } from '@/ui/atoms/switch/Switch'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { BigBlocksBanner } from '@/ui/molecules/big-blocks-banner/BigBlocksBanner'
import { SupplyApyTooltip } from '@/ui/molecules/supply-apy-tooltip/SupplyApyTooltip'
import { ConnectCTAPanel } from '@/ui/organisms/connect-cta-panel/ConnectCTAPanel'
import { cn } from '@/ui/utils/style'
import { TransactionErrorPanel } from '@/ui/molecules/transaction-error-panel/TransactionErrorPanel'
import { normalizeDecimalInput } from '@/utils/decimalInput'
import { useDebounce } from '@/utils/useDebounce'
import { TransactionLabel, useSimulateEVCBatchCall } from '@eulerxyz/wagmi-evc'
import type {
  DepositTransactionItem,
  EnableCollateralTransactionItem,
  TransactionItems,
  WrapTransactionItem,
} from '@eulerxyz/wagmi-evc'
import { useQueryClient } from '@tanstack/react-query'
import { buildPermit2BatchRequest } from '@/domain/euler/evc/buildPermit2BatchRequest'
import { usePrivy } from '@privy-io/react-auth'
import { Check, ChevronDown } from 'lucide-react'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type Address, formatUnits, parseUnits, zeroAddress } from 'viem'
import BigNumber from 'bignumber.js'
import { useAccount, useBalance, useConfig, useReadContract, useWriteContract } from 'wagmi'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { ValidationAlert } from '@/domain/euler/validation/components/ValidationAlert'
import { validateMaxDeposit } from '@/domain/euler/validation/validateEulerSupply'
import { SupplyCapDetail } from '@/ui/molecules/supply-cap-detail/SupplyCapDetail'
import { usePreviewSupplyAPY } from '@/features/elend/logic/usePreviewSupplyAPY'

// =============================================================================
// Types
// =============================================================================

interface CollateralOption {
  vaultAddress: Address
  underlyingAsset: Address
  symbol: string
  decimals: number
  logoURI?: string
  priceUSD: number
  walletBalance: string
  /** If true, this option uses native HYPE and wraps to WHYPE */
  isNativeHype?: boolean
  /** Supply APY for this vault */
  supplyAPY?: number
  /** Lending APY (from borrower interest) */
  lendingAPY?: number
  /** Intrinsic APY (from underlying asset yield) */
  intrinsicAPY?: number
  /** Market name (e.g. "HypurrFi Yield", "HypurrFi Prime") */
  marketName?: string
  /** Whether this vault is a valid collateral for the position's controller */
  isControllerCompatible?: boolean
}

interface EAddToPositionDialogContentProps {
  closeDialog: () => void
  subAccountId: number
  /** Controller (liability vault) if position has debt */
  controller?: Address | null
  currentDebtUSD: number
  currentLTV: number
  liquidationLTV: number
  /** Pre-select a specific collateral vault in the dropdown */
  initialVaultAddress?: Address
}

// Helper to create unique key for vault options (handles native vs wrapped)
function getOptionKey(option: CollateralOption): string {
  return `${option.vaultAddress}-${option.isNativeHype ? 'native' : 'erc20'}`
}

// =============================================================================
// Transaction Builder
// =============================================================================

function buildAddToPositionTransactions({
  vault,
  asset,
  decimals,
  symbol,
  amount,
  address,
  subAccountId,
  enableAsCollateral,
  useNativeHype,
}: {
  vault: Address
  asset: Address
  decimals: number
  symbol: string
  amount: bigint
  address: Address
  subAccountId: number
  enableAsCollateral: boolean
  useNativeHype: boolean
}): TransactionItems {
  if (!address || address === zeroAddress || amount <= 0n) {
    return []
  }

  const position = getSubAccount(address, subAccountId)
  const transactions: TransactionItems = []

  const assetInfo = {
    address: asset,
    name: symbol,
    symbol: symbol,
    decimals: decimals,
  }

  if (useNativeHype) {
    // CRITICAL: Must use WRAP label for native HYPE - DepositTransactionItem with isWrap gets dropped by factory
    const wrapTransaction: WrapTransactionItem = {
      label: TransactionLabel.WRAP,
      vault,
      asset: assetInfo,
      from: address,
      to: position,
      value: amount.toString(),
      totalAssets: '0',
      totalShares: '0',
    }
    transactions.push(wrapTransaction)
  } else {
    const depositTransaction: DepositTransactionItem = {
      label: TransactionLabel.DEPOSIT,
      vault,
      asset: assetInfo,
      from: address,
      to: position,
      value: amount.toString(),
    }
    transactions.push(depositTransaction)
  }

  // 2. Enable as collateral if requested
  if (enableAsCollateral) {
    const enableCollateralTransaction: EnableCollateralTransactionItem = {
      label: TransactionLabel.ENABLE_COLLATERAL,
      vault,
      asset: assetInfo,
      from: position,
      to: position,
    }
    transactions.push(enableCollateralTransaction)
  }

  return transactions
}

// No separate VaultSelector component needed - using inline dropdown like repay dialog

// =============================================================================
// Main Component
// =============================================================================

export function EAddToPositionDialogContent({
  closeDialog,
  subAccountId,
  controller,
  currentDebtUSD,
  currentLTV,
  liquidationLTV,
  initialVaultAddress,
}: EAddToPositionDialogContentProps) {
  const { address } = useAccount()
  const queryClient = useQueryClient()
  const { connectWallet } = usePrivy()
  const { name: positionName } = usePositionName(subAccountId)
  const invalidateSubaccounts = useSubaccountStore((state) => state.invalidate)

  // Fetch borrow pairs to get compatible collateral vaults
  const { pairs, isLoading: isPairsLoading } = useBorrowPairs(address as Address | undefined)

  // Fetch wallet token balances
  // Use allTokens for price lookup (includes zero-balance tokens with prices)
  // Use tokens (walletTokens) for balance lookup
  const { tokens: walletTokens, allTokens, isLoading: isWalletLoading } = useWalletTokenBalances(E_LEND_CHAIN_ID)

  // Fetch eLend data to get supply APY for vaults
  const { vaults: elendVaults } = useELend(address)

  // Create a map of vault address -> APY + market data
  const vaultApyMap = useMemo(() => {
    const map = new Map<
      string,
      { supplyAPY?: number; lendingAPY?: number; intrinsicAPY?: number; marketName?: string }
    >()
    for (const vault of elendVaults) {
      map.set(vault.address.toLowerCase(), {
        supplyAPY: vault.supplyAPY,
        lendingAPY: vault.lendingAPY,
        intrinsicAPY: vault.intrinsicAPY,
        marketName: vault.market?.name,
      })
    }
    return map
  }, [elendVaults])

  // Build collateral options from borrow pairs filtered by controller (liability),
  // but when initialVaultAddress is from a different market, include all vaults from that market.
  const collateralOptions = useMemo((): CollateralOption[] => {
    if (!pairs || pairs.length === 0) return []

    // Create a map of collateral vault -> pair info
    const collateralMap = new Map<
      string,
      {
        vaultAddress: Address
        underlyingAsset: Address
        symbol: string
        decimals: number
        logoURI?: string
        isControllerCompatible: boolean
      }
    >()

    for (const pair of pairs) {
      if (controller && pair.liability.toLowerCase() !== controller.toLowerCase()) {
        continue
      }

      if (
        !pair.collateral ||
        !pair.collateralUnderlying ||
        !pair.collateralSymbol ||
        pair.collateralDecimals === undefined
      ) {
        continue
      }

      const key = pair.collateral.toLowerCase()
      if (!collateralMap.has(key)) {
        collateralMap.set(key, {
          vaultAddress: pair.collateral,
          underlyingAsset: pair.collateralUnderlying,
          symbol: pair.collateralSymbol,
          decimals: pair.collateralDecimals,
          logoURI: pair.collateralTokenInfo?.logoURI,
          isControllerCompatible: true,
        })
      }
    }

    // When initialVaultAddress is not in controller-filtered options,
    // find its market and include all vaults from that market
    if (initialVaultAddress && !collateralMap.has(initialVaultAddress.toLowerCase())) {
      const initialVault = elendVaults.find((v) => v.address.toLowerCase() === initialVaultAddress.toLowerCase())
      if (initialVault?.market?.key) {
        const marketKey = initialVault.market.key
        for (const v of elendVaults) {
          if (v.market?.key !== marketKey) continue
          if (!v.underlyingAsset || collateralMap.has(v.address.toLowerCase())) continue
          collateralMap.set(v.address.toLowerCase(), {
            vaultAddress: v.address,
            underlyingAsset: v.underlyingAsset,
            symbol: v.assetSymbol ?? v.symbol,
            decimals: v.decimals,
            logoURI: v.underlyingTokenInfo?.logoURI,
            isControllerCompatible: false,
          })
        }
      }
    }

    // Create a map of token address -> wallet balance (only non-zero balances)
    // Use .toFixed() (not .toString()) so tiny dust balances don't get serialized in scientific
    // notation (e.g. "2.5e-13"), which would crash parseUnits downstream when Max is clicked.
    const walletBalanceMap = new Map<string, string>()
    for (const wt of walletTokens) {
      walletBalanceMap.set(wt.token.address.toLowerCase(), wt.balance.toFixed())
    }

    // Create a map of token address -> price (includes zero-balance tokens)
    const priceMap = new Map<string, number>()
    for (const wt of allTokens) {
      priceMap.set(wt.token.address.toLowerCase(), Number(wt.token.unitPriceUsd))
    }

    // Build final options with wallet balances, supply APY, and market name
    const options: CollateralOption[] = []
    for (const [, collateral] of collateralMap) {
      const walletBalance = walletBalanceMap.get(collateral.underlyingAsset.toLowerCase()) ?? '0'
      const priceUSD = priceMap.get(collateral.underlyingAsset.toLowerCase()) ?? 0
      const apyData = vaultApyMap.get(collateral.vaultAddress.toLowerCase())
      options.push({
        ...collateral,
        priceUSD,
        walletBalance,
        isNativeHype: false,
        supplyAPY: apyData?.supplyAPY,
        lendingAPY: apyData?.lendingAPY,
        intrinsicAPY: apyData?.intrinsicAPY,
        marketName: apyData?.marketName,
        isControllerCompatible: collateral.isControllerCompatible,
      })

      // If this is a WHYPE vault, also add native HYPE option
      if (isWhypeVault(collateral.underlyingAsset, E_LEND_CHAIN_ID)) {
        options.push({
          vaultAddress: collateral.vaultAddress,
          underlyingAsset: collateral.underlyingAsset,
          symbol: 'HYPE',
          decimals: 18,
          logoURI: collateral.logoURI,
          priceUSD,
          walletBalance: '0',
          isNativeHype: true,
          supplyAPY: apyData?.supplyAPY,
          lendingAPY: apyData?.lendingAPY,
          intrinsicAPY: apyData?.intrinsicAPY,
          marketName: apyData?.marketName,
          isControllerCompatible: collateral.isControllerCompatible,
        })
      }
    }

    // Return unsorted - sorting done in optionsWithNativeBalance after native balance is known
    return options
  }, [pairs, controller, walletTokens, allTokens, vaultApyMap, initialVaultAddress, elendVaults])

  // State - use key instead of index to handle re-sorting
  const [selectedVaultKey, setSelectedVaultKey] = useState<string | null>(null)
  const [isTokenDropdownOpen, setIsTokenDropdownOpen] = useState(false)
  const [depositAmount, setDepositAmount] = useState('')
  const [enableAsCollateral, setEnableAsCollateral] = useState(true)

  // Debounce deposit amount for simulation
  const [keyCounter, setKeyCounter] = useState(0)
  const prevDepositAmountRef = useRef(depositAmount)

  useEffect(() => {
    if (prevDepositAmountRef.current !== depositAmount) {
      prevDepositAmountRef.current = depositAmount
      setKeyCounter((prev) => prev + 1)
    }
  }, [depositAmount])

  const { debouncedValue: debouncedDepositAmount, isDebouncing } = useDebounce(depositAmount, `deposit-${keyCounter}`, {
    delay: 300,
  })

  // Fetch native HYPE balance (for any potential WHYPE vaults in options)
  const hasWhypeVault = collateralOptions.some((o) => isWhypeVault(o.underlyingAsset, E_LEND_CHAIN_ID))
  const { data: nativeBalanceData } = useBalance({
    address: address,
    chainId: E_LEND_CHAIN_ID,
    query: {
      enabled: !!address && hasWhypeVault,
    },
  })

  // Calculate native HYPE balance with gas reserve.
  // Use BigNumber + .toFixed() (not Number + .toString()) so we never lose precision on large
  // balances and never emit scientific notation for tiny ones (which would crash parseUnits
  // downstream when Max is clicked).
  const GAS_RESERVE = new BigNumber(0.001)
  const nativeBalanceBN = nativeBalanceData
    ? new BigNumber(formatUnits(nativeBalanceData.value, 18))
    : new BigNumber(0)
  const effectiveNativeBalance = BigNumber.max(0, nativeBalanceBN.minus(GAS_RESERVE)).toFixed()

  // Update collateral options with native HYPE balance and sort by USD value
  const optionsWithNativeBalance = useMemo(() => {
    const options = collateralOptions.map((option) => {
      if (option.isNativeHype) {
        return {
          ...option,
          walletBalance: effectiveNativeBalance,
        }
      }
      return option
    })

    // Sort by wallet balance (USD value) descending - done here after native balance is known
    return options.sort((a, b) => {
      const aUsd = Number(a.walletBalance) * a.priceUSD
      const bUsd = Number(b.walletBalance) * b.priceUSD
      return bUsd - aUsd
    })
  }, [collateralOptions, effectiveNativeBalance])

  // Get selected vault from options by key, or default to initialVaultAddress match, or first option
  const selectedVault = useMemo(() => {
    if (selectedVaultKey) {
      const found = optionsWithNativeBalance.find((o) => getOptionKey(o) === selectedVaultKey)
      if (found) return found
    }
    if (initialVaultAddress) {
      const match = optionsWithNativeBalance.find(
        (o) => o.vaultAddress.toLowerCase() === initialVaultAddress.toLowerCase() && !o.isNativeHype,
      )
      if (match) return match
    }
    return optionsWithNativeBalance[0]
  }, [optionsWithNativeBalance, selectedVaultKey, initialVaultAddress])
  const hasMultipleTokens = optionsWithNativeBalance.length > 1
  const hasMultipleMarkets = useMemo(() => {
    const markets = new Set(optionsWithNativeBalance.map((o) => o.marketName).filter(Boolean))
    return markets.size > 1
  }, [optionsWithNativeBalance])

  const canEnableAsCollateral = selectedVault?.isControllerCompatible !== false

  // Auto-toggle collateral switch based on vault compatibility
  useEffect(() => {
    setEnableAsCollateral(canEnableAsCollateral)
  }, [canEnableAsCollateral])

  // Fetch maxDeposit for selected vault to check supply cap
  const { data: maxDepositRaw } = useReadContract({
    address: selectedVault?.vaultAddress as Address,
    abi: eVaultImplementationAbi,
    functionName: 'maxDeposit',
    args: address ? [address] : ['0x0000000000000000000000000000000000000000'],
    chainId: E_LEND_CHAIN_ID,
    query: {
      enabled: !!selectedVault?.vaultAddress,
    },
  })

  // Convert maxDeposit to NormalizedUnitNumber
  const maxDeposit = useMemo(() => {
    if (maxDepositRaw === undefined || !selectedVault) return undefined
    return NormalizedUnitNumber(formatUnits(maxDepositRaw, selectedVault.decimals))
  }, [maxDepositRaw, selectedVault])

  // Derive useNativeHype from selected option
  const useNativeHype = selectedVault?.isNativeHype ?? false

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setIsTokenDropdownOpen(false)
    if (isTokenDropdownOpen) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [isTokenDropdownOpen])

  // Get wallet balance from selected collateral option
  const walletBalance = selectedVault ? Number(selectedVault.walletBalance) : 0

  // Calculate deposit value and new LTV
  const depositAmountNum = Number(depositAmount) || 0
  const depositValueUSD = depositAmountNum * (selectedVault?.priceUSD || 0)
  // Note: Only calculate new LTV if adding as collateral
  const newLTV = useMemo(() => {
    if (!enableAsCollateral || depositValueUSD <= 0) return currentLTV

    // If current LTV is 0 but there's debt (edge case: debt with no collateral)
    if (currentLTV === 0 && currentDebtUSD > 0) {
      return (currentDebtUSD / depositValueUSD) * 100
    }

    // Normal case: calculate new LTV based on increased collateral
    if (currentLTV > 0) {
      const currentCollateralUSD = currentDebtUSD / (currentLTV / 100)
      return (currentDebtUSD / (currentCollateralUSD + depositValueUSD)) * 100
    }

    return currentLTV
  }, [enableAsCollateral, depositValueUSD, currentLTV, currentDebtUSD])

  // Calculate health factors
  const currentHealthFactor = currentLTV > 0 ? liquidationLTV / currentLTV : Number.POSITIVE_INFINITY
  const newHealthFactor = newLTV > 0 ? liquidationLTV / newLTV : Number.POSITIVE_INFINITY

  const currentHealthStatus = getHealthStatus(currentHealthFactor)
  const newHealthStatus = getHealthStatus(newHealthFactor)

  // Amount in base units (for display/validation)
  const depositAmountBigInt = useMemo(() => {
    if (!selectedVault || !depositAmount || Number(depositAmount) <= 0) return 0n
    try {
      return parseUnits(depositAmount, selectedVault.decimals)
    } catch {
      return 0n
    }
  }, [depositAmount, selectedVault])

  // Debounced amount in base units (for simulation)
  const debouncedDepositAmountBigInt = useMemo(() => {
    if (!selectedVault || !debouncedDepositAmount || Number(debouncedDepositAmount) <= 0) return 0n
    try {
      return parseUnits(debouncedDepositAmount, selectedVault.decimals)
    } catch {
      return 0n
    }
  }, [debouncedDepositAmount, selectedVault])

  // Validate deposit against supply cap / maxDeposit
  const supplyValidation = useMemo(() => {
    return validateMaxDeposit({
      depositAmount,
      maxDeposit,
      symbol: selectedVault?.symbol,
    })
  }, [depositAmount, maxDeposit, selectedVault?.symbol])

  const hasEnoughBalance = depositAmountNum > 0 && depositAmountNum <= walletBalance

  const { newAPY: previewNewAPY, isLoading: isPreviewAPYLoading } = usePreviewSupplyAPY({
    vaultAddress: selectedVault?.vaultAddress ?? (zeroAddress as Address),
    changeAmount: depositAmountBigInt,
    changeType: 'deposit',
    chainId: E_LEND_CHAIN_ID,
    enabled: !!selectedVault && depositAmountBigInt > 0n,
  })

  // Build transaction items (uses debounced amount for simulation)
  const transactions = useMemo(() => {
    if (!address || address === zeroAddress || !selectedVault || debouncedDepositAmountBigInt <= 0n) {
      return []
    }

    return buildAddToPositionTransactions({
      vault: selectedVault.vaultAddress,
      asset: useNativeHype ? WHYPE_ADDRESS_HYPEREVM : selectedVault.underlyingAsset,
      decimals: selectedVault.decimals,
      symbol: selectedVault.symbol,
      amount: debouncedDepositAmountBigInt,
      address,
      subAccountId,
      enableAsCollateral,
      useNativeHype,
    })
  }, [address, selectedVault, debouncedDepositAmountBigInt, subAccountId, enableAsCollateral, useNativeHype])

  // Create token for Permit2 (not needed for native HYPE)
  const permit2Token = useMemo(() => {
    if (useNativeHype || !selectedVault) return undefined
    return new Token({
      address: CheckedAddress(selectedVault.underlyingAsset),
      symbol: TokenSymbol(selectedVault.symbol),
      name: selectedVault.symbol,
      decimals: selectedVault.decimals,
      unitPriceUsd: selectedVault.priceUSD.toString(),
    })
  }, [useNativeHype, selectedVault])

  // Permit2 preflight for ERC20 deposits
  const permit2 = usePermit2Preflight({
    token: permit2Token,
    spender: selectedVault?.vaultAddress as Address,
    amount: debouncedDepositAmountBigInt,
    enabled: !useNativeHype && hasEnoughBalance && debouncedDepositAmountBigInt > 0n && !!address,
  })

  // Simulate batch
  const config = useConfig()
  const chainId = E_LEND_CHAIN_ID
  const {
    data: simulationData,
    error: simulationError,
    isLoading: isSimulating,
  } = useSimulateEVCBatchCall({
    config,
    transactions,
    value: useNativeHype ? debouncedDepositAmountBigInt : undefined,
    query: {
      enabled: !!address && transactions.length > 0 && (useNativeHype || permit2.status === 'ready'),
      retry: false,
    },
  })

  // Write contract
  const { writeContract, isPending: isWriting, error: writeError, data: txHash, reset: resetWrite } = useWriteContract()

  // Wait for transaction
  const {
    isLoading: isWaiting,
    isSuccess: isConfirmed,
    error: waitError,
  } = useWaitForTransactionReceiptUniversal({
    hash: txHash,
  })

  // Invalidate cache on success
  useEffect(() => {
    if (isConfirmed) {
      invalidateSubaccounts()
      for (const queryKey of getWagmiReadQueryKeys()) {
        void queryClient.invalidateQueries({ queryKey, refetchType: 'all' })
      }
    }
  }, [isConfirmed, invalidateSubaccounts, queryClient])

  // Execute
  const handleExecute = useCallback(async () => {
    if (!simulationData?.request) {
      return
    }
    resetWrite()
    try {
      // For native HYPE, just execute directly with value
      if (useNativeHype) {
        const executionRequest = {
          ...simulationData.request,
          value: debouncedDepositAmountBigInt,
        }
        writeContract(executionRequest as any)
        return
      }

      // For ERC20, add Permit2 calls before the batch
      const executionRequest = buildPermit2BatchRequest({
        simulationData,
        permitTx: permit2.status === 'ready' ? permit2.permitTx : null,
        chainId,
      })
      writeContract(executionRequest as any)
    } catch {
      // Error handled by writeError
    }
  }, [
    simulationData,
    writeContract,
    resetWrite,
    useNativeHype,
    debouncedDepositAmountBigInt,
    permit2.permitTx,
    permit2.status,
    chainId,
  ])

  // Timeout tracking
  const timeout = useActionTimeout({
    timeoutMs: 15000,
    isLoading: isWriting || isWaiting || isSimulating,
    isSuccess: isConfirmed,
    error: writeError || waitError || simulationError || permit2.error,
  })

  const handleExecuteWithTimeout = useCallback(() => {
    timeout.handleActionClick(handleExecute)
  }, [timeout, handleExecute])

  // Create token for ActionRow
  const depositToken = useMemo(() => {
    if (!selectedVault) return null
    return new Token({
      symbol: TokenSymbol(useNativeHype ? 'HYPE' : selectedVault.symbol),
      name: useNativeHype ? 'HYPE' : selectedVault.symbol,
      decimals: selectedVault.decimals,
      address: CheckedAddress(selectedVault.underlyingAsset),
      unitPriceUsd: selectedVault.priceUSD.toString(),
      logoURI: selectedVault.logoURI,
    })
  }, [selectedVault, useNativeHype])

  // Determine action statuses for Permit2 flow
  const approveStatus: ActionHandlerState['status'] = useMemo(() => {
    if (useNativeHype) return 'success' // Skip Permit2 for native HYPE
    if (permit2.status === 'idle') return 'disabled'
    if (permit2.status === 'needs_approval') return 'ready'
    if (permit2.status === 'approving' || permit2.isApproving) return 'loading'
    return 'success'
  }, [permit2.status, permit2.isApproving, useNativeHype])

  const signStatus: ActionHandlerState['status'] = useMemo(() => {
    if (useNativeHype) return 'success' // Skip Permit2 for native HYPE
    if (permit2.status === 'idle') return 'disabled'
    if (permit2.status === 'needs_approval' || permit2.status === 'approving' || permit2.isApproving) return 'disabled'
    if (permit2.status === 'needs_signature') return 'ready'
    if (permit2.status === 'signing' || permit2.isSigning) return 'loading'
    return 'success'
  }, [permit2.status, permit2.isApproving, permit2.isSigning, useNativeHype])

  const executeStatus: ActionHandlerState['status'] = useMemo(() => {
    if (isWriting || isWaiting) return 'loading'
    if (!hasEnoughBalance || depositAmountBigInt <= 0n) return 'disabled'

    // Check supply cap validation
    if (!supplyValidation.canProceed) return 'disabled'

    // Show loading during debounce/simulation to prevent executing with stale data
    if (isDebouncing || isSimulating) return 'loading'

    if (useNativeHype) {
      if (!simulationData?.request) return 'disabled'
      return 'ready'
    }

    // ERC20: need permit2 flow + simulation data
    if (permit2.status !== 'ready') return 'disabled'
    if (!simulationData?.request) return 'disabled'
    return 'ready'
  }, [
    isWriting,
    isWaiting,
    simulationData?.request,
    hasEnoughBalance,
    depositAmountBigInt,
    isDebouncing,
    isSimulating,
    useNativeHype,
    permit2.status,
    supplyValidation.canProceed,
  ])

  // Show connect wallet UI if not connected
  if (!address) {
    const icons = assets.walletIcons
    const WALLET_ICONS_PATHS = [icons.metamask, icons.walletConnect, icons.coinbase]
    return (
      <MultiPanelDialog>
        <DialogPanel>
          <div className="flex items-center justify-center py-8">
            <ConnectCTAPanel
              header="Connect your wallet to add to position"
              iconPaths={WALLET_ICONS_PATHS}
              action={connectWallet}
              buttonText="Connect wallet"
            />
          </div>
        </DialogPanel>
      </MultiPanelDialog>
    )
  }

  // Loading state
  const isLoadingOptions = isPairsLoading || isWalletLoading

  // Success view
  if (isConfirmed && depositToken) {
    return (
      <MultiPanelDialog>
        <DialogTitle>{enableAsCollateral ? 'Collateral Added' : 'Savings Added'}</DialogTitle>
        <DialogPanel>
          <AddToPositionSuccessView
            token={depositToken}
            amount={NormalizedUnitNumber(debouncedDepositAmount || '0')}
            isCollateral={enableAsCollateral}
            onProceed={closeDialog}
            proceedText="Done"
          />
        </DialogPanel>
      </MultiPanelDialog>
    )
  }

  // Loading options
  if (isLoadingOptions) {
    return (
      <MultiPanelDialog>
        <DialogPanel>
          <Typography variant="h3" className="mb-4">
            Add to {positionName}
          </Typography>
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        </DialogPanel>
      </MultiPanelDialog>
    )
  }

  // No collateral options available
  if (collateralOptions.length === 0) {
    return (
      <MultiPanelDialog>
        <DialogPanel>
          <Typography variant="h3" className="mb-4">
            Add to {positionName}
          </Typography>
          <div className="py-8 text-center">
            <p className="text-muted-foreground">No compatible collateral tokens found for this position.</p>
            <Button onClick={closeDialog} variant="secondary" className="mt-4">
              Close
            </Button>
          </div>
        </DialogPanel>
      </MultiPanelDialog>
    )
  }

  const tokenImage = selectedVault
    ? getTokenImage(TokenSymbol(selectedVault.symbol)) || selectedVault.logoURI
    : undefined
  const error = permit2.error || simulationError || writeError || waitError

  return (
    <MultiPanelDialog>
      <DialogTitle className="font-semibold text-white text-xl">Add to {positionName}</DialogTitle>
      <DialogPanel>
        {/* Token + Amount combined card with dropdown */}
        <div className="relative mb-4">
          <div className="flex w-full items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            {/* Left side: Token selector */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                if (hasMultipleTokens && !isWriting && !isWaiting) setIsTokenDropdownOpen(!isTokenDropdownOpen)
              }}
              disabled={isWriting || isWaiting}
              className={cn(
                'flex items-center gap-3',
                hasMultipleTokens && !isWriting && !isWaiting && 'cursor-pointer',
                (isWriting || isWaiting) && 'opacity-50',
              )}
            >
              {tokenImage && (
                <Image
                  src={tokenImage}
                  alt={selectedVault?.symbol || ''}
                  width={32}
                  height={32}
                  className="rounded-full"
                />
              )}
              <div className="flex flex-col items-start">
                <span className="text-muted-foreground text-xs">Token to Add</span>
                <div className="flex items-center gap-1">
                  <span className="font-medium text-sm text-white">{selectedVault?.symbol}</span>
                  {hasMultipleTokens && (
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 text-muted-foreground transition-transform',
                        isTokenDropdownOpen && 'rotate-180',
                      )}
                    />
                  )}
                </div>
              </div>
            </button>

            {/* Right side: Amount input */}
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  value={depositAmount}
                  onChange={(e) => {
                    const normalized = normalizeDecimalInput(e.target.value)
                    if (normalized === null) return

                    const decimalPart = normalized.split('.')[1] || ''
                    if (selectedVault && decimalPart.length > selectedVault.decimals) {
                      const truncated = `${normalized.split('.')[0]}.${decimalPart.slice(0, selectedVault.decimals)}`
                      setDepositAmount(truncated)
                      return
                    }

                    setDepositAmount(normalized)
                  }}
                  placeholder="0.00"
                  disabled={isWriting || isWaiting}
                  className="w-32 bg-transparent text-right font-semibold text-lg text-white outline-none placeholder:text-muted-foreground disabled:opacity-50"
                />
                <button
                  // Use the source string directly — going through Number → .toString()
                  // would re-emit scientific notation for tiny dust balances and crash parseUnits.
                  onClick={() => setDepositAmount(selectedVault?.walletBalance ?? '0')}
                  disabled={isWriting || isWaiting}
                  className="rounded-lg bg-white/5 px-2 py-0.5 text-muted-foreground text-xs disabled:cursor-not-allowed hover:bg-white/10 disabled:opacity-50"
                >
                  MAX
                </button>
              </div>
              <span className="text-muted-foreground text-xs">
                Balance: {walletBalance.toLocaleString(undefined, { maximumFractionDigits: 4 })}
              </span>
            </div>
          </div>

          {/* Dropdown menu */}
          {hasMultipleTokens && isTokenDropdownOpen && (
            <div className="absolute top-full left-0 z-50 mt-2 w-full overflow-hidden rounded-xl border border-white/[0.08] bg-[#1a1a1a] shadow-2xl">
              {optionsWithNativeBalance.map((option: CollateralOption) => {
                const optionKey = getOptionKey(option)
                const isSelected = selectedVault && getOptionKey(selectedVault) === optionKey
                const optionImage = getTokenImage(TokenSymbol(option.symbol)) || option.logoURI
                return (
                  <button
                    type="button"
                    key={optionKey}
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedVaultKey(optionKey)
                      setIsTokenDropdownOpen(false)
                    }}
                    className={cn(
                      'flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-white/[0.06]',
                      isSelected && 'bg-primary/10',
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <TokenIcon
                        token={{
                          logoURI: optionImage,
                          symbol: TokenSymbol(option.symbol),
                        }}
                        className="h-7 w-7"
                      />
                      <div className="flex flex-col items-start">
                        <span className="font-medium text-sm text-white">
                          {option.symbol}
                          {option.isNativeHype && <span className="ml-1 text-muted-foreground text-xs">(native)</span>}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {option.supplyAPY !== undefined && (
                            <span className="text-primary">{option.supplyAPY.toFixed(2)}% APY</span>
                          )}
                          {hasMultipleMarkets && option.marketName && (
                            <>
                              {option.supplyAPY !== undefined && <span> · </span>}
                              {option.marketName}
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col items-end">
                        <span className="font-medium text-sm text-white">
                          {Number(option.walletBalance).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 4,
                          })}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          $
                          {(Number(option.walletBalance) * option.priceUSD).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                      {isSelected && <Check className="h-4 w-4 text-primary" />}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Collateral vs Savings toggle */}
        <div className="mb-4 flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
          <div>
            <span className="text-sm text-white">Enable as Collateral</span>
            <p className="text-muted-foreground text-xs">
              {!canEnableAsCollateral
                ? 'This vault is not a valid collateral for this position'
                : enableAsCollateral
                  ? 'Deposit will back your borrowing position'
                  : 'Deposit as savings (not used as collateral)'}
            </p>
          </div>
          <Switch
            checked={enableAsCollateral}
            onCheckedChange={setEnableAsCollateral}
            disabled={isWriting || isWaiting || !canEnableAsCollateral}
          />
        </div>

        {/* Transaction Details */}
        <div className="mb-4 flex flex-col gap-2">
          <TransactionOverviewDetailsItem label="Type">
            {enableAsCollateral ? 'Collateral' : 'Savings'}
          </TransactionOverviewDetailsItem>
          {selectedVault?.supplyAPY !== undefined && selectedVault?.lendingAPY !== undefined && (
            <TransactionOverviewDetailsItem label="Supply APY">
              <div className="flex items-center gap-1.5">
                <SupplyApyTooltip
                  breakdown={{
                    lendingApy: selectedVault.lendingAPY,
                    intrinsicApy: selectedVault.intrinsicAPY,
                    totalApy: selectedVault.supplyAPY,
                  }}
                >
                  <span className="font-medium text-green-400">{selectedVault.supplyAPY.toFixed(2)}%</span>
                </SupplyApyTooltip>
                {previewNewAPY !== undefined && depositAmountBigInt > 0n && !isPreviewAPYLoading && (
                  <ApyChange
                    currentAPY={selectedVault.supplyAPY ?? 0}
                    newAPY={addIntrinsicAPY(previewNewAPY, selectedVault.intrinsicAPY)}
                    type="earning"
                    className="font-medium"
                  />
                )}
              </div>
            </TransactionOverviewDetailsItem>
          )}
          {selectedVault && (
            <SupplyCapDetail
              vaultAddress={selectedVault.vaultAddress}
              decimals={selectedVault.decimals}
              symbol={selectedVault.symbol}
            />
          )}
          {enableAsCollateral && currentLTV > 0 && (
            <>
              <TransactionOverviewDetailsItem label="Current Health Factor">
                <div className="flex flex-col items-end gap-0.5">
                  <div className="flex items-center gap-2">
                    <currentHealthStatus.icon className={cn('h-4 w-4', currentHealthStatus.color)} />
                    <span className={cn('font-semibold', currentHealthStatus.color)}>
                      {formatHealthFactorValue(currentHealthFactor)}
                    </span>
                  </div>
                  <span className="text-muted-foreground text-xs">LTV: {formatLTV(currentLTV)}</span>
                </div>
              </TransactionOverviewDetailsItem>
              <TransactionOverviewDetailsItem label="New Health Factor (est.)">
                <div className="flex flex-col items-end gap-0.5">
                  <div className="flex items-center gap-2">
                    <newHealthStatus.icon className={cn('h-4 w-4', newHealthStatus.color)} />
                    <span className={cn('font-semibold', newHealthStatus.color)}>
                      ~{formatHealthFactorValue(newHealthFactor)}
                    </span>
                  </div>
                  <span className="text-muted-foreground text-xs">LTV: ~{formatLTV(newLTV)}</span>
                </div>
              </TransactionOverviewDetailsItem>
            </>
          )}
        </div>

        {/* Supply Cap Validation */}
        <ValidationAlert validation={supplyValidation} className="mb-4" />

        {/* Simulation Status */}
        {isSimulating && (
          <div className="mb-4 flex items-center gap-2 text-muted-foreground text-sm">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            Simulating transaction...
          </div>
        )}

        {/* Insufficient Balance Warning */}
        {!hasEnoughBalance && depositAmountNum > 0 && (
          <div className="mb-4 rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3">
            <p className="text-sm text-yellow-400">
              Insufficient balance. You have {walletBalance.toLocaleString(undefined, { maximumFractionDigits: 4 })}{' '}
              {useNativeHype ? 'HYPE' : selectedVault?.symbol}.
            </p>
          </div>
        )}

        {/* Error Display */}
        {!timeout.isTimedOut && <TransactionErrorPanel error={error} className="mt-0 mb-4" />}

        {/* Timeout error */}
        <TimeoutErrorDisplay
          isTimedOut={timeout.isTimedOut}
          onRetry={handleExecuteWithTimeout}
          isRetryDisabled={isWriting || isWaiting || !simulationData?.request}
        />

        <BigBlocksBanner className="mt-4" />

        {/* Actions Grid */}
        <div className="mt-6 grid grid-cols-[auto_auto_1fr_auto] gap-x-4">
          {/* Step 1: Approve Permit2 (only for ERC20) */}
          <ActionRow index={1}>
            <ActionRow.Icon path={assets.actions.approve} actionStatus={approveStatus} />
            <ActionRow.Title
              icon={depositToken ? <TokenIcon token={depositToken} className="h-6" /> : undefined}
              actionStatus={approveStatus}
            >
              Approve Permit2
            </ActionRow.Title>
            <ActionRow.Description successMessage="Approved!" actionStatus={approveStatus} variant="compact" />
            <ActionRow.Action onAction={() => permit2.approvePermit2?.()} status={approveStatus}>
              Approve
            </ActionRow.Action>
          </ActionRow>

          {/* Step 2: Sign Permit (only for ERC20) */}
          <ActionRow index={2}>
            <ActionRow.Icon path={assets.actions.approve} actionStatus={signStatus} />
            <ActionRow.Title
              icon={depositToken ? <TokenIcon token={depositToken} className="h-6" /> : undefined}
              actionStatus={signStatus}
            >
              Sign Permit
            </ActionRow.Title>
            <ActionRow.Description successMessage="Signed!" actionStatus={signStatus} variant="compact" />
            <ActionRow.Action onAction={() => permit2.signPermit2?.()} status={signStatus}>
              Sign
            </ActionRow.Action>
          </ActionRow>

          {/* Step 3: Add Collateral/Savings */}
          <ActionRow index={3}>
            <ActionRow.Icon path={assets.actions.deposit} actionStatus={executeStatus} />
            <ActionRow.Title
              icon={depositToken ? <TokenIcon token={depositToken} className="h-6" /> : undefined}
              actionStatus={executeStatus}
            >
              {enableAsCollateral ? 'Add Collateral' : 'Add Savings'}
            </ActionRow.Title>
            <ActionRow.Description
              successMessage={enableAsCollateral ? 'Collateral added!' : 'Savings added!'}
              actionStatus={executeStatus}
              variant="compact"
            />
            <ActionRow.Action onAction={handleExecuteWithTimeout} status={executeStatus}>
              {timeout.isTimedOut ? 'Retry' : isWriting || isWaiting ? 'Confirming...' : 'Add'}
            </ActionRow.Action>
          </ActionRow>
        </div>
      </DialogPanel>
    </MultiPanelDialog>
  )
}
